// The first four steps of any "add a vehicle" flow: Make → Model → Variant →
// Year, plus the model-code search that short-circuits all four
// (SOLD_VEHICLE_WIZARD_PLAN.md §B1).
//
// EXTRACTED FROM VehiclePickerModal, not copied. Two wizards need to identify a
// vehicle against the carmakes catalogue — the in-stock one that has existed for
// months, and the sold/reserved one being built — and a second copy of the
// fetch-and-pick logic would drift the moment either changed. The picker still
// owns everything from step 5 on; this owns steps 1-4 and nothing else.
//
// The hook is separate from the renderer because the PARENT owns the step number.
// The picker runs 6 or 7 steps, the sold-vehicle wizard runs 12, and both need
// their own indicator and Back button to stay in charge. So the hook takes an
// `onAdvance(step)` callback rather than setting a step itself.

import { useState, useEffect, useMemo, useRef } from "react";
import { Search } from "lucide-react";
import { apiFetch } from "../api.js";
import { mapFuelType, mapTransmission, mapDriveType, sanitizeCode } from "../carmakesMap";

// ── Shared presentational bits ───────────────────────────────────────────────

export const getMakeLogoUrl = (make) => {
  if (make.logo_url) return make.logo_url;
  const slug = make.name
    .toLowerCase()
    .replace(/\s+/g, '-')        // spaces → hyphens
    .replace(/[^a-z0-9-]/g, ''); // strip special chars
  return `https://raw.githubusercontent.com/filippofilip95/car-logos-dataset/master/logos/optimized/${slug}.png`;
};

// Dot-only step indicator — scales cleanly to any step count
//
// `mb-1`, not the `mb-5` this had. These utilities resolve on BOOTSTRAP's scale,
// not Tailwind's — bootstrap-compat.css keeps Bootstrap 5's `!important`
// spacing so the 275KB dependency could be dropped without reflowing ~254
// sites — and the two diverge from step 3 up. `mb-5` reads as Tailwind's 20px
// and rendered as Bootstrap's 48px, which is why the dots sat so far off the
// question below them. Step 1 is 4px on both scales, so this line means what it
// says whichever way the shim eventually goes.
export function StepIndicator({ step, total }) {
  return (
    <div className="flex items-center gap-1 mb-1">
      {Array.from({ length: total }, (_, i) => {
        const num     = i + 1;
        const done    = step > num;
        const current = step === num;
        return (
          <div key={num} className="flex items-center gap-1 flex-1 last:flex-none">
            <div
              className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold border-2 shrink-0"
              style={
                current
                  ? { borderColor: "var(--theme-accent)", background: "var(--theme-accent-tint)", color: "var(--theme-accent-ink)" }
                  : done
                  ? { borderColor: "var(--theme-accent)", background: "var(--theme-accent)", color: "var(--theme-accent-fg)" }
                  : { borderColor: "var(--theme-line)", background: "var(--theme-surface)", color: "var(--theme-muted)" }
              }
            >
              {done ? "✓" : num}
            </div>
            {i < total - 1 && (
              <div
                className="h-0.5 flex-1 rounded"
                style={{ background: done ? "var(--theme-accent)" : "var(--theme-line)" }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

export function SearchBox({ value, onChange, placeholder, autoFocus = true }) {
  return (
    <div className="relative mb-3">
      <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: "var(--theme-muted)" }} />
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="input-t"
        style={{ paddingLeft: "2rem" }}
        autoFocus={autoFocus}
      />
    </div>
  );
}

/** The year a catalogue entry went on sale, or null. */
export const launchYear = (v) =>
  v?.date_of_launch ? new Date(v.date_of_launch).getFullYear() : null;

/**
 * Does this variant belong to `year`?
 *
 * THE BOUNDS COME FROM THE SERVER, stamped on every row as `year_from`/`year_to`
 * by helpers/variantWindows.js, which is where the rule and the reason for it
 * live. This used to be computed here over the whole variant list — which is
 * precisely what stopped being downloadable.
 *
 * A variant the catalogue has no launch date for has no bounds, and is shown
 * whatever year is asked for: not knowing when a grade appeared is no reason to
 * hide it from someone looking for it.
 */
export const coversYear = (v, year) =>
  v?.year_from == null || (v.year_from <= year && year <= v.year_to);

/** "2016–2019" for a superseded batch, "2019–2026" for the current one. */
export const yearSpan = (v) =>
  v?.year_from == null ? null
    : v.year_to > v.year_from ? `${v.year_from}–${v.year_to}`
    : `${v.year_from}`;

export function VariantRow({ v }) {
  return (
    <>
      <div className="text-sm font-medium" style={{ color: "var(--theme-fg)" }}>{v.version || v.model_code || "—"}</div>
      <div className="text-xs flex flex-wrap gap-x-3 mt-0.5" style={{ color: "var(--theme-muted)" }}>
        {v.model_code      && <span className="font-mono">{v.model_code}</span>}
        {v.body_type       && <span>{v.body_type}</span>}
        {v.displacement_cc && <span>{v.displacement_cc}cc</span>}
        {v.fuel_type       && <span>{mapFuelType(v.fuel_type) || v.fuel_type}</span>}
        {v.transmission    && <span>{mapTransmission(v.transmission) || v.transmission}</span>}
        {v.driving_wheel   && <span>{mapDriveType(v.driving_wheel) || v.driving_wheel}</span>}
        {yearSpan(v)
          ? <span>{yearSpan(v)}</span>
          : v.date_of_launch && <span>{launchYear(v)}</span>}
      </div>
    </>
  );
}

// Hover styling is inline because these buttons are themed off CSS variables and
// there is no :hover in inline styles. Factored out so four call sites agree.
const hoverIn = (e) => {
  e.currentTarget.style.borderColor = "var(--theme-accent-border)";
  e.currentTarget.style.background  = "var(--theme-accent-tint)";
};
const hoverOut = (e) => {
  e.currentTarget.style.borderColor = "var(--theme-line)";
  e.currentTarget.style.background  = "";
};

// ── The hook ─────────────────────────────────────────────────────────────────

/**
 * Catalogue state for steps 1-4.
 * @param {(step: number) => void} onAdvance called with the step to move to when
 *        the user picks something. The parent decides what that step means.
 */
export function useVehicleIdentity(onAdvance) {
  const [makes, setMakes]       = useState([]);
  const [models, setModels]     = useState([]);
  const [variants, setVariants] = useState([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const [search, setSearch]     = useState("");

  //Step 3's year filter: "" is every year. A string because it comes off a
  //<select>; compared as a number everywhere it is used.
  const [variantYear, setVariantYear] = useState("");
  //What /carmakes/variants said about the model as a whole, as opposed to about
  //the rows it sent. `complete` is the one that decides how step 3 behaves: true
  //means `variants` IS the model and every further filter is a local array
  //operation, which is the case for 2,359 of the catalogue's 2,477 models.
  const [variantYears,    setVariantYears]    = useState([]);
  const [variantTotal,    setVariantTotal]    = useState(0);
  const [variantsComplete, setVariantsComplete] = useState(true);
  const [variantTruncated, setVariantTruncated] = useState(false);

  //The request currently on the wire, as a value. Every route that changes what
  //step 3 should be showing sets this, and the one effect below does the fetch —
  //rather than three effects that each have to know not to tread on the others.
  //Setting it to a fresh object is what asks for a round trip, so returning the
  //previous object unchanged is how a no-op is expressed.
  const [variantQuery, setVariantQuery] = useState(null);

  const [codeMode, setCodeMode]       = useState(false);
  const [codeQuery, setCodeQuery]     = useState("");
  const [codeResults, setCodeResults] = useState([]);
  const [codeLoading, setCodeLoading] = useState(false);
  const codeDebounce = useRef(null);

  const [selectedMake, setSelectedMake]       = useState(null);
  const [selectedModel, setSelectedModel]     = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [year, setYear]                       = useState("");

  useEffect(() => {
    setLoading(true); setError(null);
    apiFetch(`/carmakes/makes`, { method: "GET" })
      .then(r => r.json())
      .then(({ data }) => setMakes(data || []))
      .catch(() => setError("Could not load makes. Check your connection."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedMake) return;
    setLoading(true); setError(null);
    apiFetch(`/carmakes/models?make_id=${selectedMake.id}`, { method: "GET" })
      .then(r => r.json())
      .then(({ data }) => setModels(data || []))
      .catch(() => setError("Could not load models."))
      .finally(() => setLoading(false));
  }, [selectedMake]);

  //A counter, not a cancel: `apiFetch` has no abort, and two answers to two
  //requests can arrive in either order. Only the newest is allowed to write.
  const variantSeq = useRef(0);

  useEffect(() => {
    if (!variantQuery) return;
    const seq = ++variantSeq.current;
    const { modelId, year, q } = variantQuery;

    const qs = new URLSearchParams({ model_id: String(modelId) });
    if (year) qs.set("year", String(year));
    if (q)    qs.set("q", q);

    setLoading(true); setError(null);
    apiFetch(`/carmakes/variants?${qs}`, { method: "GET" })
      .then(r => r.json())
      .then((res) => {
        if (seq !== variantSeq.current) return;
        setVariants(res.data || []);
        setVariantYears(res.years || []);
        setVariantTotal(res.total ?? (res.data || []).length);
        //A server too old to answer with the flag would be handled as complete,
        //which is the behaviour this screen had before the endpoint was bounded.
        setVariantsComplete(res.complete !== false);
        setVariantTruncated(!!res.truncated);
        //The server picks the year when the model is too big to hand over whole
        //and none was asked for. Adopting it here is what puts that year in the
        //control, and it must not start another request — hence this sets the
        //year alone and never `variantQuery`.
        if (res.year != null) setVariantYear(String(res.year));
      })
      .catch(() => { if (seq === variantSeq.current) setError("Could not load variants."); })
      .finally(() => { if (seq === variantSeq.current) setLoading(false); });
  }, [variantQuery]);

  //Searching a model whose rows are NOT all here has to be the server's search,
  //or it would quietly look only at the one year that happens to be loaded and
  //report "No variants found" for a grade the catalogue holds. Under 2 characters
  //means no search, which is the same threshold the model-code box uses.
  useEffect(() => {
    if (variantsComplete || !selectedModel) return;
    const q = search.trim().length >= 2 ? search.trim() : "";
    const t = setTimeout(() => {
      //`year` is taken from the CONTROL and not from the request that is being
      //replaced. The request's own year is blank whenever the server chose it,
      //so carrying that forward would drop the dealer back to the newest line-up
      //every time they cleared the search box instead of to the year they picked.
      setVariantQuery(prev => (
        !prev || prev.q === q ? prev : { ...prev, q, year: q ? "" : variantYear }
      ));
    }, 300);
    return () => clearTimeout(t);
    //`variantYear` is read above but cannot re-fire this: on a year change `q` is
    //unchanged, so the updater returns `prev` and nothing is requested.
  }, [search, variantsComplete, selectedModel, variantYear]);

  // Debounced model-code search
  useEffect(() => {
    clearTimeout(codeDebounce.current);
    if (codeQuery.length < 2) { setCodeResults([]); return; }
    codeDebounce.current = setTimeout(() => {
      setCodeLoading(true);
      apiFetch(`/carmakes/search?q=${encodeURIComponent(codeQuery)}`, { method: "GET" })
        .then(r => r.json())
        .then(({ data }) => setCodeResults(data || []))
        .catch(() => setCodeResults([]))
        .finally(() => setCodeLoading(false));
    }, 300);
    return () => clearTimeout(codeDebounce.current);
  }, [codeQuery]);

  const pickMake = (make) => {
    setSelectedMake(make); setSelectedModel(null); setSelectedVariant(null);
    setYear(""); setSearch(""); setVariantYear(""); onAdvance(2);
  };

  const pickModel = (model) => {
    setSelectedModel(model); setSelectedVariant(null); setYear("");
    setSearch(""); setVariantYear("");
    //Cleared rather than carried over: they describe the model being left, and
    //step 3 would draw last model's year options against this model's rows for
    //as long as the fetch takes.
    setVariants([]); setVariantYears([]); setVariantTotal(0);
    setVariantsComplete(true); setVariantTruncated(false);
    setVariantQuery({ modelId: model.id, year: "", q: "" });
    onAdvance(3);
  };

  /**
   * The year control. Local when the browser holds the whole model, a round trip
   * when it does not — and the caller does not need to know which.
   */
  const chooseVariantYear = (y) => {
    setVariantYear(y);
    if (!variantsComplete) {
      //`q: ""` because the search that a server-side search ran spans every year,
      //so a year cannot narrow it; the control is disabled while one is running
      //and this is the belt to that brace.
      setVariantQuery(prev => (prev ? { ...prev, year: y, q: "" } : prev));
    }
  };

  //THE FILTERED YEAR WINS. `date_of_launch` is when the GRADE appeared, not when
  //the car was built: pick a 2018 Premio out of the batch launched in 2016 and
  //the launch year seeds step 4 with 2016, which is wrong and which a dealer in a
  //hurry will accept. Someone who narrowed the list to 2018 has already said what
  //the model year is, so use it. With no filter this is the old behaviour exactly.
  const pickVariant = (variant) => {
    setSelectedVariant(variant);
    setYear(
      variantYear ||
      (variant.date_of_launch ? launchYear(variant).toString() : "")
    );
    onAdvance(4);
  };

  // Model-code search result — the variant row already carries make/model from a JOIN
  const pickVariantFromCode = (result) => {
    setSelectedMake({ id: result.make_id, name: result.make_name });
    setSelectedModel({ id: result.model_id, name: result.model_name });
    setSelectedVariant(result);
    setYear(result.date_of_launch ? new Date(result.date_of_launch).getFullYear().toString() : "");
    setCodeMode(false); onAdvance(4);
  };

  const skipVariant = () => { setSelectedVariant("skip"); setYear(variantYear || ""); onAdvance(4); };

  const clearSearch = () => setSearch("");

  /**
   * Forget the chosen vehicle, keeping the loaded catalogue.
   *
   * For a caller that runs this flow more than once without remounting — the
   * sold-vehicle wizard's "Record another".
   *
   * NOT the thing that stops the last car's identity reaching the next one: every
   * route to step 4 already overwrites all three fields, because `pickMake` and
   * `pickModel` each clear everything downstream of themselves and all of
   * pickVariant / pickVariantFromCode / skipVariant set the year. Walking the
   * steps is what replaces the selection, and it cannot be skipped — steps 1-3
   * advance only by clicking. Do not mistake this for the safety mechanism.
   *
   * What it actually clears is the residue: the previous car's model and variant
   * LISTS, which would otherwise flash on screen at step 2 before the new fetch
   * lands, plus a stale error banner, search text and open code-search. `makes` is
   * deliberately kept — that fetch has already happened and says nothing about
   * which car this is.
   */
  const reset = () => {
    setModels([]); setVariants([]); setVariantQuery(null);
    setVariantYears([]); setVariantTotal(0);
    setVariantsComplete(true); setVariantTruncated(false);
    setSelectedMake(null); setSelectedModel(null); setSelectedVariant(null);
    setYear(""); setSearch(""); setVariantYear(""); setError(null);
    setCodeMode(false); setCodeQuery(""); setCodeResults([]);
  };

  return {
    makes, models, variants, loading, error, search, setSearch, clearSearch,
    variantYear, setVariantYear: chooseVariantYear,
    variantYears, variantTotal, variantsComplete, variantTruncated,
    codeMode, setCodeMode, codeQuery, setCodeQuery, codeResults, codeLoading,
    selectedMake, selectedModel, selectedVariant, year, setYear,
    pickMake, pickModel, pickVariant, pickVariantFromCode, skipVariant, reset,
  };
}

/**
 * What steps 1-4 produced, in fields.js terms. The shape both wizards feed into
 * their vehicle record.
 *
 * ONE KEY HERE IS NOT A fields.js KEY: `ModelCode`. See below.
 */
export function identityToFields(id) {
  const hasVariant = id.selectedVariant && id.selectedVariant !== "skip";
  const v = id.selectedVariant;
  return {
    Make:      id.selectedMake?.name || "",
    Model:     id.selectedModel?.name || "",
    ModelYear: id.year ? parseInt(id.year, 10) : null,
    ...(hasVariant && {
      //NOT ChassisNo, which is what this was until 2026-08-27 and is how a model
      //code came to be filed as a chassis number. `NZE141G` identifies the MODEL;
      //`NZE141G-9185659` identifies the car. fields.js asks for 8-20 characters,
      //so the short codes were caught and every code long enough to pass — most of
      //them, once the `DBA-` prefix is counted — went through silently.
      //
      //Handed out under its own name so a caller can offer it as a prefix and ask
      //for the serial that completes it. It must not reach a vehicle record: the
      //composer in SoldVehicleWizard.jsx consumes it, and buildPrefill in
      //VehiclePickerModal.jsx drops it.
      ModelCode:    sanitizeCode(v.model_code),
      Shape:        v.body_type   || "",
      EngineType:   sanitizeCode(v.engine_code),
      FuelType:     mapFuelType(v.fuel_type),
      Transmission: mapTransmission(v.transmission),
      Drive:        mapDriveType(v.driving_wheel),
      // Only when the catalogue actually has a value, so the form's own numeric
      // defaults survive an absent one.
      ...(v.displacement_cc != null && { EngineDisplacement: v.displacement_cc }),
      ...(v.doors           != null && { Doors:  v.doors }),
      ...(v.riding_capacity != null && { Seats:  v.riding_capacity }),
      ...(v.weight_kg       != null && { Weight: v.weight_kg }),
    }),
  };
}

// ── The renderer ─────────────────────────────────────────────────────────────

/** Renders whichever of steps 1-4 the parent says is current. */
export default function VehicleIdentitySteps({ step, identity: id }) {
  const filteredMakes = id.makes.filter(m => m.name.toLowerCase().includes(id.search.toLowerCase()));
  const filteredModels = id.models.filter(m => m.name.toLowerCase().includes(id.search.toLowerCase()));
  //A year the list cannot offer is treated as no filter at all. It should not
  //arise — every route to a new variant list clears the year — but a <select>
  //whose value matches no option displays its FIRST option, so a stale year
  //would leave the control reading "All years" over an empty list. Reading the
  //year back out of the offered set is what keeps the two agreeing.
  const offeredYears = useMemo(
    () => new Set(id.variantYears.map(y => y.year)), [id.variantYears]);
  const wantedYear = id.variantYear ? parseInt(id.variantYear, 10) : null;
  const pickedYear = wantedYear != null && offeredYears.has(wantedYear) ? wantedYear : null;

  //A search the SERVER ran, across every year of a model too big to hand over.
  //While one is running the year control has nothing to say, and says so.
  const serverSearching = !id.variantsComplete && id.search.trim().length >= 2;

  //WHO FILTERS. When the browser holds the whole model it filters, exactly as
  //this screen always did. When it does not, the rows it holds are already the
  //answer to the year or the search it asked for, and filtering them again here
  //would only hide rows the server deliberately sent.
  const filteredVariants = !id.variantsComplete ? id.variants : id.variants.filter(v =>
    ((v.version    || "").toLowerCase().includes(id.search.toLowerCase()) ||
     (v.body_type  || "").toLowerCase().includes(id.search.toLowerCase()) ||
     (v.model_code || "").toLowerCase().includes(id.search.toLowerCase()))
    && (pickedYear == null || coversYear(v, pickedYear))
  );

  if (step === 1) {
    return (
      <>
        <div className="flex gap-2 mb-3">
          <button
            onClick={() => { id.setCodeMode(false); id.setCodeQuery(""); }}
            className="flex-1 py-1.5 rounded-lg text-xs font-medium border transition-colors"
            style={!id.codeMode
              ? { background: "var(--theme-accent-tint)", borderColor: "var(--theme-accent-border)", color: "var(--theme-accent-ink)" }
              : { background: "transparent", borderColor: "var(--theme-line)", color: "var(--theme-muted)" }}
          >
            Browse by Make
          </button>
          <button
            onClick={() => id.setCodeMode(true)}
            className="flex-1 py-1.5 rounded-lg text-xs font-medium border transition-colors"
            style={id.codeMode
              ? { background: "var(--theme-accent-tint)", borderColor: "var(--theme-accent-border)", color: "var(--theme-accent-ink)" }
              : { background: "transparent", borderColor: "var(--theme-line)", color: "var(--theme-muted)" }}
          >
            Search by Model Code
          </button>
        </div>

        {id.codeMode ? (
          <>
            <SearchBox value={id.codeQuery} onChange={id.setCodeQuery} placeholder="e.g. DAA-AXVH70…" />
            <div className="overflow-y-auto max-h-64 flex flex-col gap-1 pr-1">
              {id.codeLoading && (
                <div className="text-center text-sm py-6" style={{ color: "var(--theme-muted)" }}>Searching…</div>
              )}
              {!id.codeLoading && id.codeQuery.length >= 2 && id.codeResults.length === 0 && (
                <div className="text-center text-sm py-6" style={{ color: "var(--theme-muted)" }}>No matches found</div>
              )}
              {!id.codeLoading && id.codeResults.map(r => (
                <button
                  key={r.id}
                  onClick={() => id.pickVariantFromCode(r)}
                  className="text-left px-4 py-2.5 rounded-xl border transition-all"
                  style={{ borderColor: "var(--theme-line)" }}
                  onMouseEnter={hoverIn}
                  onMouseLeave={hoverOut}
                >
                  <div className="text-xs mb-0.5" style={{ color: "var(--theme-muted)" }}>{r.make_name} · {r.model_name}</div>
                  <VariantRow v={r} />
                </button>
              ))}
              {!id.codeLoading && id.codeQuery.length < 2 && (
                <div className="text-center text-sm py-6" style={{ color: "var(--theme-muted)" }}>Type at least 2 characters</div>
              )}
            </div>
          </>
        ) : (
          <>
            <SearchBox value={id.search} onChange={id.setSearch} placeholder="Search makes…" />
            {id.loading ? (
              <div className="flex items-center justify-center h-48 text-sm" style={{ color: "var(--theme-muted)" }}>Loading…</div>
            ) : (
              <div className="overflow-y-auto max-h-64 grid grid-cols-2 sm:grid-cols-3 gap-2 pr-1">
                {filteredMakes.map(make => (
                  <button
                    key={make.id}
                    onClick={() => id.pickMake(make)}
                    className="text-left p-3 rounded-xl border transition-all"
                    style={{ borderColor: "var(--theme-line)" }}
                    onMouseEnter={hoverIn}
                    onMouseLeave={hoverOut}
                  >
                    <img
                      src={getMakeLogoUrl(make)}
                      alt={make.name}
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      className="h-10 w-auto object-contain mx-auto mb-1"
                    />
                    <div className="font-semibold text-sm truncate" style={{ color: "var(--theme-fg)" }}>{make.name}</div>
                    {make.country_of_origin && (
                      <div className="text-xs truncate" style={{ color: "var(--theme-muted)" }}>{make.country_of_origin}</div>
                    )}
                    {make.popularity > 0 && (
                      <div className="text-xs mt-0.5" style={{ color: "var(--theme-accent-ink)" }}>{make.popularity} in stock</div>
                    )}
                  </button>
                ))}
                {filteredMakes.length === 0 && (
                  <div className="col-span-3 text-center text-sm py-8" style={{ color: "var(--theme-muted)" }}>No makes found</div>
                )}
              </div>
            )}
          </>
        )}
      </>
    );
  }

  if (step === 2) {
    return (
      <>
        <div className="text-sm mb-2" style={{ color: "var(--theme-muted)" }}>
          Make: <span className="font-semibold" style={{ color: "var(--theme-fg)" }}>{id.selectedMake?.name}</span>
        </div>
        <SearchBox value={id.search} onChange={id.setSearch} placeholder="Search models…" />
        {id.loading ? (
          <div className="flex items-center justify-center h-48 text-sm" style={{ color: "var(--theme-muted)" }}>Loading…</div>
        ) : (
          <div className="overflow-y-auto max-h-64 flex flex-col gap-1 pr-1">
            {filteredModels.map(model => (
              <button
                key={model.id}
                onClick={() => id.pickModel(model)}
                className="text-left px-4 py-2.5 rounded-xl border transition-all text-sm font-medium"
                style={{ borderColor: "var(--theme-line)", color: "var(--theme-fg)" }}
                onMouseEnter={(e) => { hoverIn(e); e.currentTarget.style.color = "var(--theme-accent-ink)"; }}
                onMouseLeave={(e) => { hoverOut(e); e.currentTarget.style.color = "var(--theme-fg)"; }}
              >
                {model.name}
              </button>
            ))}
            {filteredModels.length === 0 && (
              <div className="text-center text-sm py-8" style={{ color: "var(--theme-muted)" }}>No models found</div>
            )}
          </div>
        )}
      </>
    );
  }

  if (step === 3) {
    return (
      <>
        <div className="text-sm mb-2" style={{ color: "var(--theme-muted)" }}>
          <span className="font-semibold" style={{ color: "var(--theme-fg)" }}>{id.selectedMake?.name} {id.selectedModel?.name}</span>
        </div>
        <div className="flex gap-2 items-start">
          <div className="flex-1 min-w-0">
            <SearchBox value={id.search} onChange={id.setSearch} placeholder="Search variants or model codes…" />
          </div>
          {/* Offered only when the catalogue has more than one line-up to choose
              between — on a model with a single launch date it would filter
              nothing and only take up room the search box wants on a phone.

              `w-auto` and not `sm:w-auto`: bootstrap-compat.css carries
              `.w-auto { width: auto !important }`, and without that !important
              `.input-t`'s own `width: 100%` wins and the select eats the row.

              `appearance: auto` restores the drop-down chevron that Tailwind's
              preflight resets away — without it this reads as a second text box
              beside the search one, which is the one thing it is not. Same fix,
              and same reason, as `.rec-costbar select.input-t` in carstyles.css. */}
          {id.variantYears.length > 0 && (
            <select
              aria-label="Model year"
              className="input-t w-auto shrink-0 mb-3 text-sm"
              style={{ appearance: "auto", WebkitAppearance: "auto", padding: "0 6px 0 8px" }}
              disabled={serverSearching}
              value={pickedYear == null ? "" : String(pickedYear)}
              onChange={e => id.setVariantYear(e.target.value)}
            >
              {/* No "all" when the browser does not hold them all: an option
                  that cannot be honoured is worse than one that is not there. */}
              {id.variantsComplete && <option value="">All years ({id.variantTotal})</option>}
              {id.variantYears.map(({ year, count }) => (
                <option key={year} value={year}>{year} ({count})</option>
              ))}
            </select>
          )}
        </div>
        {id.loading ? (
          <div className="flex items-center justify-center h-48 text-sm" style={{ color: "var(--theme-muted)" }}>Loading…</div>
        ) : (
          <>
          {(filteredVariants.length < id.variantTotal || id.variantTruncated) && (
            <div className="text-xs mb-1" style={{ color: "var(--theme-muted)" }}>
              {serverSearching
                ? <>{id.variantTruncated ? <>First {filteredVariants.length} matches</> : <>{filteredVariants.length} of {id.variantTotal}</>} across all years</>
                : <>Showing {filteredVariants.length} of {id.variantTotal}
                    {pickedYear != null && <> on sale in {pickedYear}</>}</>}
              {/* Said plainly, because a list that silently stops is how a dealer
                  concludes the catalogue does not have their car. */}
              {id.variantTruncated && <> — narrow the search to see the rest</>}
            </div>
          )}
          <div className="overflow-y-auto max-h-64 flex flex-col gap-1 pr-1">
            <button
              onClick={id.skipVariant}
              className="text-left px-4 py-2.5 rounded-xl border border-dashed transition-all text-sm italic"
              style={{ borderColor: "var(--theme-line)", color: "var(--theme-muted)" }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "var(--theme-accent-border)";
                e.currentTarget.style.background  = "var(--theme-surface-alt)";
              }}
              onMouseLeave={hoverOut}
            >
              None of these / skip variant
            </button>
            {filteredVariants.map(v => (
              <button
                key={v.id}
                onClick={() => id.pickVariant(v)}
                className="text-left px-4 py-2.5 rounded-xl border transition-all"
                style={{ borderColor: "var(--theme-line)" }}
                onMouseEnter={hoverIn}
                onMouseLeave={hoverOut}
              >
                <VariantRow v={v} />
              </button>
            ))}
            {filteredVariants.length === 0 && (
              <div className="text-center text-sm py-4" style={{ color: "var(--theme-muted)" }}>No variants found</div>
            )}
          </div>
          </>
        )}
      </>
    );
  }

  // step 4 — confirm the year, and show what the catalogue knows
  const hasVariant = id.selectedVariant && id.selectedVariant !== "skip";
  const v = id.selectedVariant;
  return (
    <>
      <div className="text-sm mb-4" style={{ color: "var(--theme-muted)" }}>
        <span className="font-semibold" style={{ color: "var(--theme-fg)" }}>{id.selectedMake?.name} {id.selectedModel?.name}</span>
        {hasVariant && v.version && <span style={{ color: "var(--theme-muted)" }}> · {v.version}</span>}
        {hasVariant && v.model_code && (
          <span className="font-mono text-xs ml-1" style={{ color: "var(--theme-muted)" }}>({v.model_code})</span>
        )}
      </div>
      <label className="label-t block mb-1">
        Model Year <span className="hint-t font-normal">(optional)</span>
      </label>
      <input
        type="number"
        min="1900"
        max={new Date().getFullYear() + 2}
        value={id.year}
        onChange={e => id.setYear(e.target.value)}
        placeholder="e.g. 2022"
        className="input-t w-40"
        autoFocus
      />
      {hasVariant && (
        <div
          className="mt-5 p-4 rounded-xl text-xs grid grid-cols-2 gap-x-6 gap-y-1"
          style={{ background: "var(--theme-surface-alt)", border: "1px solid var(--theme-line)", color: "var(--theme-fg)" }}
        >
          {v.body_type        && <><span style={{ color: "var(--theme-muted)" }}>Body</span><span>{v.body_type}</span></>}
          {v.engine_code      && <><span style={{ color: "var(--theme-muted)" }}>Engine Code</span><span className="font-mono">{v.engine_code}</span></>}
          {v.displacement_cc  && <><span style={{ color: "var(--theme-muted)" }}>Displacement</span><span>{v.displacement_cc} cc</span></>}
          {v.fuel_type        && <><span style={{ color: "var(--theme-muted)" }}>Fuel</span><span>{mapFuelType(v.fuel_type)}</span></>}
          {v.transmission     && <><span style={{ color: "var(--theme-muted)" }}>Transmission</span><span>{mapTransmission(v.transmission) || v.transmission}</span></>}
          {v.driving_wheel    && <><span style={{ color: "var(--theme-muted)" }}>Drive</span><span>{mapDriveType(v.driving_wheel) || v.driving_wheel}</span></>}
          {v.doors         != null && <><span style={{ color: "var(--theme-muted)" }}>Doors</span><span>{v.doors}</span></>}
          {v.riding_capacity != null && <><span style={{ color: "var(--theme-muted)" }}>Seats</span><span>{v.riding_capacity}</span></>}
          {v.weight_kg     != null && <><span style={{ color: "var(--theme-muted)" }}>Weight</span><span>{v.weight_kg} kg</span></>}
          {v.model_code       && <><span style={{ color: "var(--theme-muted)" }}>Model Code</span><span className="font-mono">{v.model_code}</span></>}
        </div>
      )}
    </>
  );
}
